import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutTemplate, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';

/**
 * Renders a collapsible list of the user's saved event templates.
 * When one is picked, `onPick` receives the template `body` dict — the
 * parent form spreads that into its state to prefill the fields.
 */
const EventTemplatePicker = ({ onPick }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/event-templates`);
      setTemplates(res.data?.templates || []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleDelete = async (e, template_id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    setDeletingId(template_id);
    try {
      await axios.delete(`${API}/event-templates/${template_id}`);
      setTemplates((prev) => prev.filter((t) => t.template_id !== template_id));
      toast.success('Template deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    } finally {
      setDeletingId(null);
    }
  };

  const handlePick = (tpl) => {
    onPick?.(tpl.body || {});
    toast.success(`Prefilled from "${tpl.name}"`, {
      description: 'Adjust the date, time and any other details.',
    });
    setExpanded(false);
  };

  if (loading || templates.length === 0) return null;

  return (
    <div
      className="mb-6 rounded-2xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 overflow-hidden"
      data-testid="event-templates-picker"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-emerald-50/60 transition-colors"
        data-testid="event-templates-picker-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <LayoutTemplate className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="text-left">
            <div className="font-heading font-bold text-base">
              Start from a template
            </div>
            <div className="text-xs text-muted-foreground">
              {templates.length} saved template{templates.length !== 1 ? 's' : ''} — prefill this form in one click
            </div>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-emerald-700" />
        ) : (
          <ChevronDown className="w-5 h-5 text-emerald-700" />
        )}
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.ul
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-emerald-200 divide-y divide-emerald-100 overflow-hidden"
            data-testid="event-templates-list"
          >
            {templates.map((tpl) => (
              <li
                key={tpl.template_id}
                className="p-3 flex items-center gap-3 hover:bg-white/70 transition-colors cursor-pointer"
                onClick={() => handlePick(tpl)}
                data-testid={`template-item-${tpl.template_id}`}
              >
                {tpl.body?.image_url ? (
                  <img
                    src={tpl.body.image_url}
                    alt=""
                    className="w-12 h-12 rounded-xl object-cover shrink-0 bg-black/5"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                    <LayoutTemplate className="w-5 h-5 text-emerald-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{tpl.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {tpl.body?.category || '—'}
                    {tpl.body?.city ? ` · ${tpl.body.city}` : ''}
                    {tpl.body?.allow_guests ? ' · +1 enabled' : ''}
                    {tpl.body?.is_recurring ? ` · Recurring (${tpl.body.recurrence_type || 'custom'})` : ''}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="rounded-full text-xs"
                  onClick={(e) => { e.stopPropagation(); handlePick(tpl); }}
                  data-testid={`template-use-${tpl.template_id}`}
                >
                  Use
                </Button>
                <button
                  type="button"
                  onClick={(e) => handleDelete(e, tpl.template_id)}
                  disabled={deletingId === tpl.template_id}
                  className="p-2 rounded-full text-muted-foreground hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
                  aria-label="Delete template"
                  data-testid={`template-delete-${tpl.template_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventTemplatePicker;
